import React, {memo, useEffect, useState} from 'react';
import {Box, Tooltip,} from '@material-ui/core';
import MaterialTable from 'material-table';
import TableIcons from '../../utils/TableIcons';

import LaunchIcon from '@material-ui/icons/Launch';
import RefreshIcon from '@material-ui/icons/Refresh';
import convertToLowerCase from "../../utils/convertToLowerCase";
import convertToCurrency from "../../utils/convertToCurrency";
import {ActiveState, getStateName} from "../../utils/ActiveState";
import setActiveIconWithPaused from "../../utils/setActiveIconWithPaused";

const CampaignsTable = memo(({data, refetch}) => {
    const [columns, setColumns] = useState([]);
    /*
        term - Search Term
        rowData - rowdata from table
        key - Key that is used to reference the row data
    */
    const customFilter = (term, rowData, mainKey, subKey = false) => {
        if (subKey) {
            const value = rowData[mainKey][subKey];
            return convertToLowerCase(value).includes(convertToLowerCase(term));
        }
        return convertToLowerCase(rowData[mainKey]).includes(convertToLowerCase(term));
    };

    const CampaignBox = ({name, choiceOfLaw}) => (
        <Box display={"flex"} flexDirection={"column"}>
            <Box style={{marginBottom: '5px'}}>
                {name || 'N/A'}
            </Box>
            <Box>
                <strong>Choice Of Law &ndash;</strong> {choiceOfLaw}
            </Box>
        </Box>
    );

    const activeStateLookup = [
        {id: ActiveState.ACTIVE, title: 'Active'},
        {id: ActiveState.PAUSED, title: 'Paused'},
        {id: ActiveState.INACTIVE, title: 'Not Active'},
    ]

    useEffect(() => {
        const lookupState = {}
        activeStateLookup.map(row => {
            lookupState[row.id] = row.title
        })

        setColumns([{
            title: 'Id', field: "id",
            customFilterAndSearch: (term, rowData) => customFilter(term, rowData, 'id')
        },
            {
                title: 'Active',
                field: "activeState",
                lookup: lookupState,
                defaultFilter: [String(ActiveState.ACTIVE), String(ActiveState.PAUSED)],
                render: (rowData) => {
                    return (<Tooltip title={getStateName(rowData.activeState)}>
                        {setActiveIconWithPaused(rowData.activeState)}
                    </Tooltip>)
                }
            },
            {
                title: 'Price Point', field: "pricePoint"
            },
            {
                title: "Servicer", field: "servicer",
                customFilterAndSearch: (term, rowData) => customFilter(term, rowData, 'servicer', ['name']),
                render: rowData => (
                    <CampaignBox
                        name={rowData.servicer.name}
                        choiceOfLaw={rowData.servicer.choiceOfLaw}
                    />
                )
            },
            {
                title: "Portfolio", field: "portfolio",
                customFilterAndSearch: (term, rowData) => customFilter(term, rowData, 'portfolio', 'name'),
                render: rowData => (
                    <CampaignBox
                        name={rowData.portfolio.name}
                        choiceOfLaw={rowData.portfolio.choiceOfLaw}
                    />
                )
            },
            {
                title: "Name", field: "name",
                customFilterAndSearch: (term, rowData) => customFilter(term, rowData, 'name'),
                render: rowData => (
                    <CampaignBox
                        name={rowData.name}
                        choiceOfLaw={rowData.campaignChoiceOfLaw}
                    />
                )
            },
            {
                title: "Customer Name", field: "customerCampaignName",
                customFilterAndSearch: (term, rowData) => customFilter(term, rowData, 'customerCampaignName')
            },
            {
                title: 'Customer Id', field: "customerCampaignId",
                customFilterAndSearch: (term, rowData) => customFilter(term, rowData, 'customerCampaignId')
            },
            {
                title: "Status", field: "status",
                customFilterAndSearch: (term, rowData) => customFilter(term, rowData, 'status')
            },
            {
                title: "Campaign - Choice Of Law", field: "campaignChoiceOfLaw",
                customFilterAndSearch: (term, rowData) => customFilter(term, rowData, 'campaignChoiceOfLaw')
            },
            {
                title: "Legacy Campaigns", field: "legacyCampaigns",
                customFilterAndSearch: (term, rowData) => customFilter(term, rowData, 'legacyCampaigns'),
                render: rowData => (
                    <>
                        {!rowData.legacyCampaigns.length ? (
                            'N/A'
                        ) : (
                            <ul>
                                {rowData.legacyCampaigns.map(item => (
                                    <li key={item}>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )
            }]);
    }, []);

    return (
        <MaterialTable
            title=""
            icons={TableIcons}
            columns={columns}
            data={data.map(campaign => ({
                id: campaign.id,
                active: campaign.active,
                activeState: campaign.activeState,
                customerCampaignId: campaign.customerCampaignId,
                customerCampaignName: campaign.customerCampaignName,
                pricePoint: convertToCurrency(campaign.pricePoint),
                servicer: campaign.servicer,
                portfolio: campaign.portfolio,
                name: campaign.name,
                status: campaign.status,
                campaignChoiceOfLaw: campaign.choiceOfLaw,
                legacyCampaigns: campaign.legacyCampaigns
            }))}
            actions={[
                {
                    icon: () => <RefreshIcon/>,
                    tooltip: 'Refetch Data',
                    isFreeAction: true,
                    onClick: (event) => refetch()
                },
                {
                    icon: () => <LaunchIcon/>,
                    tooltip: 'View Campaign',
                    onClick: (event, rowData) => window.open(`/info/campaigns/${rowData.id}`)
                },
            ]}
            options={{
                pageSize: 20,
                filtering: true,
                pageSizeOptions: [20, 50, 100],
            }}
        />
    );
});

export default CampaignsTable;
